import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useFileUrl } from "./useInteraction";
import { useTranscodingPolling, useRedactionPolling } from "./useJobs";
import { JobState } from "../types/job/Job";
import { GetInteractionMediaFailureReason } from "../api/interaction";
import { useAlert } from "../context/AlertContext";
import { useTranslation } from "react-i18next";

/**
 * Calls useFileUrl (like normal) but then if no url is given
 * It polls based on jobId provided and recalls useFileUrl after job is complete
 * @param playerInteractionId the interactionId of media you want
 * @param customerId current customer
 * @returns regular useFileUrl and also isLoading and isPolling to display to user
 */
export const useFileWithTranscodePolling = (
  playerInteractionId: string | undefined,
  customerId: string | undefined
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [pollingTranscodeJobId, setPollingTranscodeJobId] = useState<
    string | undefined
  >(undefined);
  const [pollingRedactionJobId, setPollingRedactionJobId] = useState<
    string | undefined
  >(undefined);

  const fileQuery = useFileUrl(playerInteractionId, customerId);
  const transcodeStatusQuery = useTranscodingPolling(
    customerId,
    pollingTranscodeJobId
  );
  const redactionStatusQuery = useRedactionPolling(
    customerId,
    pollingRedactionJobId
  );
  const showAlert = useAlert();

  useEffect(() => {
    if (fileQuery.data) {
      const { temporaryUrl, transcodingJobId, redactionJobId, reason } =
        fileQuery.data;

      if (
        reason === GetInteractionMediaFailureReason.MISSING_REDACTED_TRANSCRIPT
      ) {
        return;
      }

      if (!temporaryUrl && transcodingJobId) {
        setPollingTranscodeJobId(transcodingJobId);
      }
      if (!temporaryUrl && redactionJobId) {
        setPollingRedactionJobId(redactionJobId);
      }
    }
  }, [fileQuery.data]);

  useEffect(() => {
    if (
      transcodeStatusQuery.data &&
      transcodeStatusQuery.data === JobState.Complete
    ) {
      setPollingTranscodeJobId(undefined); // Stop polling
      queryClient.invalidateQueries({ queryKey: ["fileUrl"] }); // Refetch the URL
    } else if (
      transcodeStatusQuery.data &&
      transcodeStatusQuery.data === JobState.Error
    ) {
      showAlert(t("errors:transcodingJobFailed"), "error");
      setPollingTranscodeJobId(undefined);
    }
  }, [
    transcodeStatusQuery.data,
    playerInteractionId,
    customerId,
    queryClient,
    showAlert,
    t,
  ]);

  useEffect(() => {
    if (
      redactionStatusQuery.data &&
      redactionStatusQuery.data === JobState.Complete
    ) {
      setPollingRedactionJobId(undefined); // Stop polling
      queryClient.invalidateQueries({ queryKey: ["fileUrl"] }); // Refetch the URL
    } else if (
      redactionStatusQuery.data &&
      redactionStatusQuery.data === JobState.Error
    ) {
      showAlert(t("errors:redactionJobFailed"), "error");
      setPollingRedactionJobId(undefined);
    }
  }, [
    redactionStatusQuery.data,
    playerInteractionId,
    customerId,
    queryClient,
    showAlert,
    t,
  ]);

  return {
    data: fileQuery.data,
    error:
      fileQuery.error ||
      transcodeStatusQuery.error ||
      redactionStatusQuery.error,
    isLoading: fileQuery.isLoading,
    isPolling: !!pollingTranscodeJobId || !!pollingRedactionJobId,
    isTranscoding: !!pollingTranscodeJobId,
    isRedacting: !!pollingRedactionJobId,
    isMissingRedactedTranscript:
      fileQuery.data?.reason ===
      GetInteractionMediaFailureReason.MISSING_REDACTED_TRANSCRIPT,
    errorMessage: fileQuery.data?.message,
  };
};
